import { useEffect, useState } from 'react';
import type { BrandTheme, LayoutMode } from '@/types/theme';

// ============= Constants =============

/** localStorage key for the layout axis (default | compact). */
export const LAYOUT_STORAGE_KEY = 'litium-theme-mode';

/** localStorage key for the brand axis (litium | winstonTaylor). */
export const BRAND_STORAGE_KEY = 'litium-brand-theme';

/** Window event broadcast when the layout axis changes. */
export const LAYOUT_CHANGE_EVENT = 'litium-theme-mode-change';

/** Window event broadcast when the brand axis changes. */
export const BRAND_CHANGE_EVENT = 'litium-brand-theme-change';

/**
 * Backwards-compatible alias for the layout storage key. Existing modules
 * import this name; keep it stable while migrating to the two-axis model.
 */
export const THEME_STORAGE_KEY = LAYOUT_STORAGE_KEY;

// ============= Storage helpers =============

const readLayoutMode = (): LayoutMode => {
  if (typeof window === 'undefined') return 'default';
  return window.localStorage.getItem(LAYOUT_STORAGE_KEY) === 'compact'
    ? 'compact'
    : 'default';
};

const readBrandTheme = (): BrandTheme => {
  if (typeof window === 'undefined') return 'litium';
  return window.localStorage.getItem(BRAND_STORAGE_KEY) === 'winstonTaylor'
    ? 'winstonTaylor'
    : 'litium';
};

/** Persist the layout axis and broadcast the change to all listeners. */
export const setLayoutMode = (mode: LayoutMode): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
  window.dispatchEvent(
    new CustomEvent(LAYOUT_CHANGE_EVENT, { detail: { mode } }),
  );
};

/** Persist the brand axis and broadcast the change to all listeners. */
export const setBrandTheme = (brand: BrandTheme): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BRAND_STORAGE_KEY, brand);
  window.dispatchEvent(
    new CustomEvent(BRAND_CHANGE_EVENT, { detail: { brand } }),
  );
};

// ============= Hook =============

export interface UseAppThemeReturn {
  brandTheme: BrandTheme;
  layoutMode: LayoutMode;
  isWinstonTaylor: boolean;
  isCompactTheme: boolean;
  /** Backwards-compatible alias for {@link layoutMode}. */
  themeMode: LayoutMode;
  setBrandTheme: (brand: BrandTheme) => void;
  setLayoutMode: (mode: LayoutMode) => void;
}

/**
 * Reads and subscribes to the two theme axes (brand + layout). Persistence is
 * via localStorage and cross-component sync is via window events, so any
 * consumer stays in sync when the header toggles either axis.
 */
export const useAppTheme = (): UseAppThemeReturn => {
  const [brandTheme, setBrandThemeState] = useState<BrandTheme>(readBrandTheme);
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(readLayoutMode);

  useEffect(() => {
    const handleLayoutChange = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: LayoutMode }>).detail;
      if (detail?.mode === 'compact' || detail?.mode === 'default') {
        setLayoutModeState(detail.mode);
      }
    };

    const handleBrandChange = (event: Event) => {
      const detail = (event as CustomEvent<{ brand?: BrandTheme }>).detail;
      if (detail?.brand === 'winstonTaylor' || detail?.brand === 'litium') {
        setBrandThemeState(detail.brand);
      }
    };

    window.addEventListener(
      LAYOUT_CHANGE_EVENT,
      handleLayoutChange as EventListener,
    );
    window.addEventListener(
      BRAND_CHANGE_EVENT,
      handleBrandChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        LAYOUT_CHANGE_EVENT,
        handleLayoutChange as EventListener,
      );
      window.removeEventListener(
        BRAND_CHANGE_EVENT,
        handleBrandChange as EventListener,
      );
    };
  }, []);

  return {
    brandTheme,
    layoutMode,
    isWinstonTaylor: brandTheme === 'winstonTaylor',
    isCompactTheme: layoutMode === 'compact',
    themeMode: layoutMode,
    setBrandTheme,
    setLayoutMode,
  };
};
